import React, { useMemo, useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { Box, LinearProgress, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { colors, spacing, typography, radius, transitions } from '../../theme/tokens';
import ConfettiCelebration from './ConfettiCelebration';
import { celebratePulse } from '../../animations/microInteractions';
import useReducedMotion from '../../hooks/useReducedMotion';

const ProgressContainer = styled(Box)(({ theme, celebrate, reducedMotion }) => ({
  width: '100%',
  marginBottom: spacing.md,
  padding: spacing.md,
  backgroundColor: colors.neutral[50],
  borderRadius: radius.card,
  border: `1px solid ${colors.border.light}`,

  // Apply celebration pulse on milestone
  ...(celebrate && celebratePulse(reducedMotion)),

  [theme.breakpoints.down('sm')]: {
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
}));

const ProgressHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: spacing.sm,
});

const ProgressLabel = styled(Typography)({
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  color: colors.text.secondary,
});

const ProgressStats = styled(Typography)({
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.semibold,
  color: colors.primary.main,
});

const StyledLinearProgress = styled(LinearProgress)(({ value, theme }) => ({
  height: '12px',
  borderRadius: radius.full,
  backgroundColor: colors.neutral[200],
  // RTL support: Transform origin for progress bar
  direction: 'ltr', // Keep LTR for consistent fill direction
  transform: 'scaleX(-1)', // Flip for RTL
  '& .MuiLinearProgress-bar': {
    borderRadius: radius.full,
    backgroundColor: value === 100 ? colors.success.main : colors.primary.main,
    transition: `all ${transitions.duration.slow} ${transitions.easing.smooth}`,
    transform: 'scaleX(-1)', // Flip back to maintain correct fill direction (right-to-left in RTL)
    transformOrigin: 'right', // RTL: Fill from right to left
  },
}));

const CompletionBadge = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.xs,
  marginTop: spacing.sm,
  padding: `${spacing.xs} ${spacing.sm}`,
  backgroundColor: colors.success.light,
  color: colors.success.dark,
  borderRadius: radius.full,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  // RTL-aware animation: Slide in from the natural reading direction
  animation: 'slideInRTL 0.4s ease-out',
  '@keyframes slideInRTL': {
    from: {
      opacity: 0,
      // RTL: Slide from right (start of reading direction)
      transform: 'translateX(20px) translateY(-10px) scale(0.95)',
    },
    '50%': {
      transform: 'translateX(0) translateY(-5px) scale(1.02)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0) translateY(0) scale(1)',
    },
  },
}));

const StepIndicator = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  gap: spacing.md,
  marginTop: spacing.md,
  flexWrap: 'wrap',
});

const StepDot = styled(Box)(({ active, completed, theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: spacing.xs,
  minWidth: '80px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '60px',
  },
  '& .dot': {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    transition: `all ${transitions.duration.normal} ${transitions.easing.standard}`,
    backgroundColor: completed
      ? colors.success.main
      : active
      ? colors.primary.main
      : colors.neutral[300],
    color: active || completed ? '#ffffff' : colors.text.secondary,
    border: active ? `3px solid ${colors.primary.light}` : 'none',
    [theme.breakpoints.down('sm')]: {
      width: '28px',
      height: '28px',
      fontSize: typography.fontSize.xs,
    },
  },
  '& .label': {
    fontSize: typography.fontSize.xs,
    fontWeight: active ? typography.fontWeight.semibold : typography.fontWeight.regular,
    color: active ? colors.text.primary : colors.text.secondary,
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      fontSize: '10px',
    },
  },
}));

/**
 * Enhanced progress component showing field completion count and step progress
 * @param {Object} props
 * @param {number} props.visibleDiv - Current step (0: Fill Form, 1: Upload Docs, 2: Submit)
 * @param {Array} props.inputFields - Array of input fields
 * @param {Array} props.requestedFiles - Array of requested file uploads
 */
export default function EnhancedProgress({ visibleDiv, inputFields = [], requestedFiles = [], requireID = false }) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();
  const [showConfetti, setShowConfetti] = useState(false);
  const [previousPercentage, setPreviousPercentage] = useState(0);
  const [milestoneReached, setMilestoneReached] = useState(null);

  // Calculate field completion stats
  const fieldStats = useMemo(() => {
    const totalFields = inputFields.filter(field => field?.mandatory === true).length;
    const filledFields = inputFields.filter(
      field =>
        field?.mandatory === true &&
        field?.value !== '' &&
        field?.value !== 'data:image/png;base64,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'
    ).length;

    return {
      total: totalFields,
      filled: filledFields,
      percentage: totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0,
    };
  }, [inputFields]);

  // Milestone celebration effect
  useEffect(() => {
    const currentPercentage = fieldStats.percentage;

    // Check if we've reached a milestone (25%, 50%, 75%, 100%)
    const milestones = [25, 50, 75, 100];
    const reachedMilestone = milestones.find(
      milestone => currentPercentage >= milestone && previousPercentage < milestone
    );

    if (reachedMilestone) {
      setMilestoneReached(reachedMilestone);

      // Show confetti only on 100% completion
      if (reachedMilestone === 100) {
        setShowConfetti(true);
      }

      // Clear milestone after animation
      const timer = setTimeout(() => {
        setMilestoneReached(null);
      }, 2000);

      return () => clearTimeout(timer);
    }

    setPreviousPercentage(currentPercentage);
  }, [fieldStats.percentage, previousPercentage]);

  const steps = [
    { label: t('enhancedProgress.fillForm', 'מילוי טופס'), value: 0 },
    ...(requestedFiles.length > 0 || requireID
      ? [{ label: t('enhancedProgress.uploadDocs', 'העלאת מסמכים'), value: 1 }]
      : []),
    { label: t('enhancedProgress.submit', 'הגשה'), value: 2 },
  ];

  const isStepCompleted = (stepValue) => {
    if (stepValue === 0) {
      return visibleDiv > 0;
    } else if (stepValue === 1) {
      return visibleDiv > 1;
    } else if (stepValue === 2) {
      return false; // Never completed until submitted
    }
    return false;
  };

  const isStepActive = (stepValue) => {
    return visibleDiv === stepValue;
  };

  // Show field progress only on step 0 (Fill Form)
  const showFieldProgress = visibleDiv === 0 && fieldStats.total > 0;

  return (
    <>
      <ProgressContainer celebrate={milestoneReached !== null} reducedMotion={reducedMotion}>
        {showFieldProgress && (
          <>
            <ProgressHeader>
              <ProgressLabel>
                {t('enhancedProgress.progressLabel', 'התקדמות מילוי השדות')}
              </ProgressLabel>
              <ProgressStats>
                {fieldStats.filled} / {fieldStats.total}
                {milestoneReached && milestoneReached !== 100 && (
                  <span style={{ marginRight: spacing.sm, fontSize: typography.fontSize.sm }}>
                    🎉 {milestoneReached}%!
                  </span>
                )}
              </ProgressStats>
            </ProgressHeader>

            <StyledLinearProgress variant="determinate" value={fieldStats.percentage} />

            {fieldStats.percentage === 100 && (
              <CompletionBadge>
                <CheckCircleIcon sx={{ fontSize: '16px' }} />
                <span>{t('enhancedProgress.allFieldsComplete', 'כל השדות מולאו!')}</span>
              </CompletionBadge>
            )}
          </>
        )}

        <StepIndicator>
          {steps.map((step, index) => (
            <StepDot key={step.value} active={isStepActive(step.value)} completed={isStepCompleted(step.value)}>
              <div className="dot">
                {isStepCompleted(step.value) ? '✓' : index + 1}
              </div>
              <div className="label">{step.label}</div>
            </StepDot>
          ))}
        </StepIndicator>
      </ProgressContainer>

      {/* Confetti celebration on 100% completion */}
      <ConfettiCelebration
        active={showConfetti}
        duration={3000}
        pieceCount={50}
        onComplete={() => setShowConfetti(false)}
      />
    </>
  );
}
